import { type Dispatch, type SetStateAction, useMemo, useState } from 'react'

import { formatToPTBRNumber } from 'commons/utils/number'
import { convertBooleanApiValue } from 'commons/utils/string'
import { Button, CircularProgress, Icon, Info, Tooltip } from 'components'
import { CentersFactories, TypesValidations } from 'constants/enums'
import STATUS from 'constants/statusRascunho'
import { ROUTES } from 'constants/urls'
import { BOX_TYPE } from 'container/ProductPage/components/ProductDetail'
import type { Material } from 'container/SalesOrder/interfaces'
import { useNavigate } from 'react-router-dom'
import { setFocusMaterialInsertInput } from 'store/StructuralContext/structuralSlice'
import { useDispatch, useSelector } from 'store/store'
import { theme } from 'styles/theme/hav-theme'

import * as S from './styles'

function goToTop() {
  window.scrollTo({ behavior: 'smooth', top: 0 })
}

export type FooterOrderProps = {
  children?: React.ReactNode
}

export const FooterOrder = ({ children }: FooterOrderProps) => {
  return (
    <S.FooterContainer>
      <S.FooterContent>{children}</S.FooterContent>
    </S.FooterContainer>
  )
}

export type MaterialFooter = {
  type: 'insert' | 'save'
  count: number
  loadingButton?: boolean
  onSaveEdit?: () => Promise<boolean>
  disabled?: boolean
  mixedBoxQtd?: number
}
export const FooterOrderMaterial = ({
  type = 'insert',
  count,
  onSaveEdit,
  disabled = false,
  loadingButton = false,
  mixedBoxQtd,
}: MaterialFooter) => {
  const dispatch = useDispatch()
  const [submited, setSubmited] = useState(false)

  const handleSaveEditMaterial = async () => {
    if (onSaveEdit && !submited) {
      setSubmited(true)
      dispatch(setFocusMaterialInsertInput(true))
      const result = await onSaveEdit()
      if (result === false) {
        setSubmited(false)
      }
    }
  }
  const { materialSelected } = useSelector((stateProduct) => stateProduct.products)

  return (
    <FooterOrder>
      <div className="contentRightBetween">
        <S.MaterialBoxes>
          <h1>Quantidade total</h1>
          <h2>
            {mixedBoxQtd
              ? `${count * mixedBoxQtd} pares / ${mixedBoxQtd} caixas`
              : materialSelected.ds_tipo_caix === BOX_TYPE.CLOSED
                ? `Pares ${count}`
                : `Caixas ${count}`}
          </h2>
        </S.MaterialBoxes>
        {type === 'insert' ? (
          <Button
            content="Adicionar materiais"
            disabled={disabled}
            fullWidth={false}
            id="btn-add-material"
            loading={loadingButton}
            onClick={handleSaveEditMaterial}
            type="submit"
          />
        ) : (
          <Button
            content="Salvar edição"
            disabled={disabled}
            fullWidth={false}
            id="btn-save-material"
            loading={loadingButton}
            onClick={handleSaveEditMaterial}
            type="submit"
          />
        )}
      </div>
    </FooterOrder>
  )
}

export type ResumeFooter = {
  type: 'proceed' | 'generateOV'
  totalOfPairs: number
  totalOfOrder: number
  onProceed?: () => void
  isProceedLoading?: boolean
  onGenerateOV?: () => void
  onSendToPlanning?: () => void
  isGenerateOVLoading?: boolean
  onDownloadReport?: () => void
  isLoadingReport?: boolean
  onEdit?: () => void
}

export const FooterOrderResume = ({
  type = 'proceed',
  totalOfOrder,
  totalOfPairs,
  onProceed,
  isProceedLoading,
  onGenerateOV,
  onSendToPlanning,
  isGenerateOVLoading,
  onDownloadReport,
  isLoadingReport,
  onEdit,
}: ResumeFooter) => {
  const { globalLoading } = useSelector((store) => store.structure)
  const { fl_pedi_antp, fl_utlz_fair_shar } = useSelector((store) => store.salesOrder.draftBody)
  const { simulateOrder, isSampleOrder } = useSelector((store) => store.salesOrder)

  const isOutOfLine = simulateOrder?.fl_matr_fora_linh === TypesValidations.YES
  const isFairShareOffice = convertBooleanApiValue(fl_utlz_fair_shar)

  const getCriticMaterials = (materials: Material[]) =>
    materials.filter((material) =>
      [STATUS.OUT_OF_STOCK, STATUS.WITHOUT_QUOTA].includes(material.stat),
    )

  const isFullA297 = useMemo(() => {
    if (!simulateOrder?.materiais?.length) return false
    const criticMaterials = getCriticMaterials(simulateOrder.materiais)
    return (
      criticMaterials.length > 0 &&
      criticMaterials.every((material) => material.cota.cd_cent_matr === CentersFactories.A297)
    )
  }, [simulateOrder])

  const canProceed = useMemo(() => {
    if (!simulateOrder?.materiais.length) return false
    if (simulateOrder.qt_tota_atnd > 0) return true
    const criticMaterials = getCriticMaterials(simulateOrder.materiais)
    if (criticMaterials.length) {
      return !isFullA297 && !isOutOfLine && !isFairShareOffice
    }
    return false
  }, [simulateOrder])

  // TODO: migrate texts to locales and use i18n
  const getTooltipMessage = () => {
    if (isFullA297)
      return 'Não é possível solicitar análise de volume extra para materiais alocados na fábrica A297. Edite ou exclua os materiais.'

    const criticMaterials = getCriticMaterials(simulateOrder?.materiais || [])
    if (criticMaterials.length > 0) {
      if (isOutOfLine)
        return 'Não é possível solicitar análise de volume extra para materiais fora de linha. Edite ou exclua os materiais.'
      if (isFairShareOffice)
        return 'Não é possível solicitar análise de volume extra. Edite ou exclua os materiais.'
    }

    return 'Não é possível prosseguir. Edite ou exclua os materiais.'
  }

  const handleDownloadReport = () => {
    if (onDownloadReport) {
      onDownloadReport()
    }
  }

  const handleProceed = () => {
    if (onProceed) {
      onProceed()
    }
  }

  const handleGenerateOV = () => {
    if (onGenerateOV) {
      onGenerateOV()
    }
  }

  const handleSendToPlanning = () => {
    if (onSendToPlanning) {
      onSendToPlanning()
    }
  }

  const handleEdit = () => {
    if (onEdit) {
      onEdit()
    }
  }

  return (
    <FooterOrder>
      <S.ResumeInfo>
        <S.ResumeSide>
          <S.ResumeLine>
            <span>Total de pares</span>
            <span>{totalOfPairs}</span>
          </S.ResumeLine>
        </S.ResumeSide>
        <S.ResumeSide>
          <S.ResumeLine>
            <span>Saldo do pedido</span>
            <div>
              <Info title="">
                <>
                  Caso haja alteração do pedido <strong>Em Análise</strong>, o valor total também se
                  modificará.
                </>
              </Info>
              <span>{formatToPTBRNumber(totalOfOrder)}</span>
            </div>
          </S.ResumeLine>
        </S.ResumeSide>
      </S.ResumeInfo>
      <S.ActionButton fullfill={false} onClick={goToTop}>
        <Icon color={theme.colors.primary.main} icon="chevron-up" size="20" /> Ir para o topo
      </S.ActionButton>
      <div className="contentRight">
        {false && (
          // !!onDownloadReport
          <S.ActionButton
            disabled={isLoadingReport}
            fullfill={false}
            onClick={handleDownloadReport}
          >
            {isLoadingReport ? <CircularProgress size="18px" /> : 'Baixar Relatório'}
          </S.ActionButton>
        )}
        <S.ActionButton
          fullfill={false}
          globalLoading={globalLoading}
          onClick={() => !globalLoading && handleEdit()}
        >
          Editar pedido
        </S.ActionButton>
        {type === 'proceed' ? (
          <>
            {!isSampleOrder && (
              <Tooltip arrow placement="top" title={!canProceed ? getTooltipMessage() : ''}>
                <div>
                  <Button
                    content="Prosseguir"
                    disabled={!canProceed}
                    loading={isProceedLoading || globalLoading}
                    onClick={() => handleProceed()}
                    width={120}
                  />
                </div>
              </Tooltip>
            )}
          </>
        ) : (
          <>
            {fl_pedi_antp === TypesValidations.YES && (
              <Button
                content="Enviar para planejamento"
                loading={isGenerateOVLoading}
                onClick={() => handleGenerateOV()}
                width={250}
              />
            )}
            {fl_pedi_antp === TypesValidations.NO && (
              <Button
                content="Gerar ordem de venda"
                loading={isGenerateOVLoading}
                onClick={() => handleSendToPlanning()}
                width={200}
              />
            )}
          </>
        )}
      </div>
    </FooterOrder>
  )
}

export type FooterSAP = {
  onSendToSAP: () => void
  onSave?: () => void
  loadingSAP: boolean
  loadingOnSave: boolean
  setModalJustification: Dispatch<SetStateAction<boolean>>
}

export const FooterOrderSAP = ({
  onSendToSAP,
  onSave,
  loadingSAP,
  loadingOnSave,
  setModalJustification,
}: FooterSAP) => {
  const { orderSuccess } = useSelector((store) => store.salesOrder)

  const checkedOvs = orderSuccess.ordems?.filter((ov) => ov.checkOv)
  const isSentToSapDisabled =
    checkedOvs && checkedOvs.length > 0
      ? !checkedOvs.every((ov) => ov.justify || ov.fl_negc_difd === TypesValidations.NO)
      : true
  const isJustificationBtnDisabled =
    checkedOvs && checkedOvs.length < 2
      ? true
      : !checkedOvs?.every((ov) => ov.fl_negc_difd === TypesValidations.YES)

  return (
    <FooterOrder>
      <S.ActionButton fullfill={false} onClick={goToTop}>
        <Icon color={theme.colors.primary.main} icon="chevron-up" size="20" />
        Ir para o topo
      </S.ActionButton>
      <div className="contentRight">
        <S.ActionButton fullfill={false} onClick={onSave}>
          {loadingOnSave ? <CircularProgress size="20px" /> : 'Salvar'}
        </S.ActionButton>
        <S.ActionButton
          disabled={isJustificationBtnDisabled}
          fullfill={false}
          onClick={() => setModalJustification(true)}
        >
          JUSTIFICAR SELECIONADOS
        </S.ActionButton>
        <Button
          content="Enviar ao SAP"
          disabled={isSentToSapDisabled}
          loading={loadingSAP}
          onClick={() => onSendToSAP()}
          width={155}
        />
      </div>
    </FooterOrder>
  )
}

export type PreOrder = {
  onDownloadReport?: () => void
  isLoadingReport?: boolean
}
export const FooterPreorder = ({ onDownloadReport, isLoadingReport }: PreOrder) => {
  const navigate = useNavigate()
  const handleDownloadReport = () => {
    if (onDownloadReport) {
      onDownloadReport()
    }
  }
  return (
    <FooterOrder>
      <div className="contentRight">
        {false && (
          <S.ActionButton
            disabled={isLoadingReport}
            fullfill={false}
            onClick={handleDownloadReport}
          >
            {isLoadingReport ? <CircularProgress size="18px" /> : 'Baixar Relatório'}
          </S.ActionButton>
        )}
        <S.ActionButton
          fullfill={false}
          onClick={() => navigate(`${ROUTES.orders.base}${ROUTES.orders.children.new.base}`)}
        >
          Criar novo pedido
        </S.ActionButton>

        <Button
          content="Ir para gerenciamento"
          onClick={() => navigate(`${ROUTES.manage.base}`)}
          width={220}
        />
      </div>
    </FooterOrder>
  )
}
